import {getApprovalBreakdown} from '@/utils/approvalStatusUtils';
import {formattedDate} from '@/utils/formattedDate';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import {
  ContentSubmission,
  ContentSubmissionVersion,
} from '@/types/contentSubmission';
import {ConversationMessage} from '@/types/conversationMessage';
import {APPROVAL_STEP_LABELS} from '@/views/campaigns/constants/approvalSteps';
import {FEED_ITEM_TYPE, FeedItem} from './aggregateActivityFeedItems';

const ACTION_LABELS: Record<string, string> = {
  accepted: 'Content approved',
  rejected: 'Changes requested',
  archived: 'Content archived',
};

const SENDER_KIND_BRAND = 'brand';

function inferVersionNumber(
  versions: ContentSubmissionVersion[],
  approvalCreatedAt: string
): number {
  const approvalTime = new Date(approvalCreatedAt).getTime();
  let bestIndex = 0;
  versions.forEach((v, i) => {
    if (v.submittedAt && new Date(v.submittedAt).getTime() <= approvalTime) {
      bestIndex = i;
    }
  });
  return bestIndex + 1;
}

/**
 * Builds the creator-facing activity feed timeline.
 *
 * Includes: CREATOR_UPLOAD, STEP_APPROVAL, ACTION, BRAND_MESSAGE, VERSION_MESSAGE
 * Excludes: COMMENT (internal brand-only comments — never shown to creator)
 *
 * @param contentSubmission  - full submission with all versions
 * @param allVersionMessages - map of versionId → messages for that version
 */
function aggregateCreatorFeedItems(
  contentSubmission: ContentSubmission,
  allVersionMessages: Record<string, ConversationMessage[]>
): FeedItem[] {
  const {versions} = contentSubmission;
  if (!versions?.length) return [];

  const items: FeedItem[] = [];

  // Oldest version first (versions are newest-first from API, so reverse)
  const orderedVersions = versions.slice().reverse();

  orderedVersions.forEach((version, index) => {
    const versionNumber = index + 1;

    // Creator upload pill
    const uploadTimestamp = version.submittedAt || version.createdAt;
    if (uploadTimestamp) {
      const dateStr = formattedDate(
        new Date(uploadTimestamp),
        MONTH_SHORT_DAY_YEAR_FORMAT
      );
      items.push({
        id: `creator-upload--${version.id}`,
        type: FEED_ITEM_TYPE.CREATOR_UPLOAD,
        createdAt: uploadTimestamp,
        formattedDate: dateStr,
        versionNumber,
        content: translate('Creator uploaded V{version} on {date}', {
          version: versionNumber,
          date: dateStr,
        }),
      });
    }

    // Action event (approve / request changes / archive)
    if (version.terminalStateForRevision && version.feedbackProvidedAt) {
      const dateStr = formattedDate(
        new Date(version.feedbackProvidedAt),
        MONTH_SHORT_DAY_YEAR_FORMAT
      );
      const label = translate(
        ACTION_LABELS[version.terminalStateForRevision] ??
          version.terminalStateForRevision
      );
      items.push({
        id: `action--${version.id}`,
        type: FEED_ITEM_TYPE.ACTION,
        createdAt: version.feedbackProvidedAt,
        formattedDate: dateStr,
        versionNumber,
        terminalState: version.terminalStateForRevision,
        content: translate('{label} V{version} on {date}', {
          label,
          version: versionNumber,
          date: dateStr,
        }),
      });
    }

    // Brand message card ("Feedback sent to creator" / archive note)
    const isLatestVersion = index === orderedVersions.length - 1;
    const isArchiveNote =
      isLatestVersion && contentSubmission.aasmState === 'archived';
    const skipBrandMessage =
      isLatestVersion && contentSubmission.aasmState === 'pending';

    if (
      version.brandMessageHtml &&
      version.feedbackProvidedAt &&
      !skipBrandMessage
    ) {
      const dateStr = formattedDate(
        new Date(version.feedbackProvidedAt),
        MONTH_SHORT_DAY_YEAR_FORMAT
      );
      items.push({
        id: `brand-message--${version.id}`,
        type: FEED_ITEM_TYPE.BRAND_MESSAGE,
        createdAt: version.feedbackProvidedAt,
        formattedDate: dateStr,
        versionNumber,
        isArchiveNote,
        brandMessageHtml: version.brandMessageHtml,
        fullName:
          contentSubmission.campaign?.brand?.displayName ??
          contentSubmission.campaign?.brand?.name ??
          null,
      });
    }
  });

  // Step approvals — infer version from timestamps
  const {approvedStepsEntries} = getApprovalBreakdown(
    contentSubmission.approvalStatus
  );
  approvedStepsEntries
    .filter((entry) => entry.createdAt)
    .forEach((entry) => {
      const timestamp = entry.createdAt!;
      const versionNumber = inferVersionNumber(orderedVersions, timestamp);
      const stepLabel = translate(
        APPROVAL_STEP_LABELS[entry.step] ?? entry.step
      );
      const dateStr = formattedDate(
        new Date(timestamp),
        MONTH_SHORT_DAY_YEAR_FORMAT
      );
      items.push({
        id: `step-approval--${entry.campaignApprovalStepId}`,
        type: FEED_ITEM_TYPE.STEP_APPROVAL,
        createdAt: timestamp,
        formattedDate: dateStr,
        versionNumber,
        step: entry.step,
        content: translate('{step} approved V{version} on {date}', {
          step: stepLabel,
          version: versionNumber,
          date: dateStr,
        }),
      });
    });

  const brandDisplayName =
    contentSubmission.campaign?.brand?.displayName ??
    contentSubmission.campaign?.brand?.name ??
    null;

  // Standalone version messages for all versions
  orderedVersions.forEach((version, index) => {
    const versionNumber = index + 1;
    const messages = allVersionMessages[version.id] ?? [];
    messages.forEach((message) => {
      const isFromBrand = message.sender?.kind === SENDER_KIND_BRAND;
      items.push({
        id: `version-message--${message.id}`,
        type: FEED_ITEM_TYPE.VERSION_MESSAGE,
        createdAt: message.createdAt,
        formattedDate: formattedDate(
          new Date(message.createdAt),
          MONTH_SHORT_DAY_YEAR_FORMAT
        ),
        versionNumber,
        content: message.body,
        // Brand messages show the brand name (consistent with BrandMessageCard);
        // creator replies show the creator's display name.
        fullName: isFromBrand
          ? brandDisplayName
          : message.signerDisplayName || message.sender?.name || null,
        isFromBrand,
        attachments: message.messageAttachments ?? [],
      });
    });
  });

  // Oldest first — chat-style ordering: latest item appears at the bottom of the feed.
  return items.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

export default aggregateCreatorFeedItems;
